import { getFirestore, collection, query, orderBy, onSnapshot, doc, getDoc, addDoc, updateDoc, limit, startAfter, getDocs } from 'firebase/firestore';
import moment from 'moment';
import api from './api';

export const chatService = {
    // Fetch user details by Firebase UID
    fetchUserDetails: async (uid) => {
        try {
            const response = await api.get(`/user/fbid/${uid}`);
            return {
                ...response.data,
                name: response.data.name,
                avatar: response.data.photos?.[0]?.photoUrl
            };
        } catch (error) {
            console.error('Error fetching user details:', error);
            return { name: 'Utilisateur', avatar: null };
        }
    },

    // Get conversation document
    getConversation: async (conversationId) => {
        const db = getFirestore();
        const conversationRef = doc(db, 'Conversations', conversationId);
        const conversationDoc = await getDoc(conversationRef);
        return { conversationRef, conversationDoc };
    },

    // Subscribe to conversation changes
    subscribeToConversation: (conversationRef, onUpdate) => {
        return onSnapshot(conversationRef, (doc) => {
            const conversationData = doc.data();
            onUpdate(conversationData);
        });
    },

    // Subscribe to messages
    subscribeToMessages: (conversationRef, currentUserId, onMessagesUpdate, limite = 20) => {
        const messagesRef = collection(conversationRef, 'Messages');
        const q = query(
            messagesRef, 
            orderBy('createdAt', 'desc'),
            limit(limite)
        );
        
        return onSnapshot(q, (snapshot) => {
            const messagesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                isUser: doc.data().user._id === currentUserId
            }));

            // Inverser l'ordre pour avoir les plus anciens en premier
            onMessagesUpdate(messagesList.reverse());
        });
    },

    // Send a new message
    sendMessage: async (conversationId, message, userId) => {
        const db = getFirestore();
        const conversationRef = doc(db, 'Conversations', conversationId);
        const messagesRef = collection(conversationRef, 'Messages');

        const newMessage = {
            createdAt: moment().utc().unix(),
            messageType: 'text',
            text: message,
            user: {
                _id: userId
            },
            _id: Date.now()
        };

        await addDoc(messagesRef, newMessage);
        await updateDoc(conversationRef, {
            latestMessage: newMessage,
            updateAt: moment().utc().unix()
        });
    },

    // Block user in conversation
    blockUser: async (conversationId, userId) => {
        const db = getFirestore();
        const conversationRef = doc(db, 'Conversations', conversationId);
        const conversationSnap = await getDoc(conversationRef);
        const conversationData = conversationSnap.data();
        
        const blockedBy = conversationData?.blockedBy || [];
        if (!blockedBy.includes(userId)) {
            blockedBy.push(userId);
        }
        
        await updateDoc(conversationRef, {
            isBlocked: true,
            blockedBy: blockedBy,
            [`blockInfo.${userId}`]: {
                blockedAt: new Date().toISOString(),
                reason: 'user_blocked'
            }
        });
    },

    // Unblock user in conversation
    unblockUser: async (conversationId, userId) => {
        const db = getFirestore();
        const conversationRef = doc(db, 'Conversations', conversationId);
        const conversationSnap = await getDoc(conversationRef);
        const conversationData = conversationSnap.data();
        
        const blockedBy = (conversationData.blockedBy || []).filter(
            blockedUserId => blockedUserId !== userId
        );
        
        await updateDoc(conversationRef, {
            blockedBy: blockedBy,
            isBlocked: blockedBy.length > 0,
            [`blockInfo.${userId}`]: null
        });
    },

    // Get older messages
    getOlderMessages: async (conversationId, oldestTimestamp, currentUserId, limite = 20) => {
        try {
            const db = getFirestore();
            const conversationRef = doc(db, 'Conversations', conversationId);
            const messagesRef = collection(conversationRef, 'Messages');
            
            const q = query(
                messagesRef,
                orderBy('createdAt', 'desc'),
                startAfter(oldestTimestamp),
                limit(limite)
            );

            const querySnapshot = await getDocs(q);
            const messages = [];
            
            querySnapshot.forEach((doc) => {
                const messageData = doc.data();
                messages.push({
                    id: doc.id,
                    ...messageData,
                    isUser: messageData.user._id === currentUserId
                });
            });

            // Inverser l'ordre pour avoir les plus anciens en premier
            return messages.reverse();
        } catch (error) {
            console.error('Error getting older messages:', error);
            throw error;
        }
    }
};

export default chatService;
